import React, { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal, Card, Row, Col, Form, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CircularProgress } from '@material-ui/core'
import DatePicker from 'react-datepicker'
import { Formik } from 'formik'
import FileUpload from '../../FileUpload'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import ActionData from '../../../types/ActionData'
import ServiceVerificationAuditForm from '../../../types/ServiceVerificationAuditForm'
import RootState from '../../../types/RootState'
import { mainColors } from '../../../assets/colors'
import { ServiceVerificationAuditSchema } from '../../../utils/validation'
import { getDateWithoutTZOffset } from '../../../utils/dataTools'
import moment from 'moment'

import './index.css'

interface Props {
  readonly: boolean;
  show: boolean;
  updatingServiceVerificationAudit: boolean;
  updateServiceVerificationAudit: ActionData;
  onClose: () => void;
  onSubmit: (values: any) => void;
}

const initialServiceVerificationAudit: ServiceVerificationAuditForm = {
  serviceVerificationAuditId: -1,
  organizationName: '',
  providerName: '',
  providerId: -1,
  taxIdNumber: '',
  claimNumber: '',
  authorizationNumber: '',
  serviceType: '',
  emailAddress: '',
  firstName: '',
  middleName: '',
  lastName: '',
  serviceFromDate: null,
  serviceToDate: null,
  quantity: null,
  totalPaidAmount: null,
  status: '',
  serviceVerificationDate: null,
  reviewerFullName: '',
  specialistFullName: '',
  additionalRecipients: null,
  internalNotes: null,
  file: [],
  removeFile: [],
  uploadedFile: [],
  activity: [],
  followUp: [],
  documentation: '',
  comments: null
}

const ServiceVerificationAuditModal: FC<Props> =
  ({
    readonly,
    show,
    updatingServiceVerificationAudit,
    updateServiceVerificationAudit,
    onClose,
    onSubmit,
  }: Props) => {
    const errorText = updateServiceVerificationAudit.error !== false ? updateServiceVerificationAudit.error.response.text : ''
    const modalText = updateServiceVerificationAudit.error !== false ? (errorText.includes('could not be notified') ? '' : 'The data could not be saved') : 'Updating Service Verification Audit'
    const loadingIcon = updateServiceVerificationAudit.error !== false ? (errorText.includes('could not be notified') ? <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} color='orange' className='modal-success-icon' /> : <FontAwesomeIcon icon={['fas', 'exclamation-circle']} color={mainColors.loadingBlue} className='modal-success-icon' />) : <CircularProgress size={70} />

    const [ initialServiceVerificationAuditValues, setInitialServiceVerificationAuditValues ] = useState(initialServiceVerificationAudit)

    const { serviceVerificationAudit } = useSelector((state: RootState) => ({
      serviceVerificationAudit: state.backgroundCheckVerification.serviceVerificationAudit.data,
    }))

    useEffect(() => {
      const serviceVerificationData = serviceVerificationAudit ? {
        serviceVerificationAuditId: serviceVerificationAudit.serviceVerificationAuditId,
        organizationName: serviceVerificationAudit.organizationName,
        providerName: serviceVerificationAudit.providerName,
        providerId: serviceVerificationAudit.providerId,
        taxIdNumber: serviceVerificationAudit.taxIdNumber,
        claimNumber: serviceVerificationAudit.claimNumber,
        authorizationNumber: serviceVerificationAudit.authorizationNumber,
        serviceType: serviceVerificationAudit.serviceType,
        emailAddress: serviceVerificationAudit.emailAddress,
        firstName: serviceVerificationAudit.firstName,
        middleName: serviceVerificationAudit.middleName,
        lastName: serviceVerificationAudit.lastName,
        serviceFromDate: serviceVerificationAudit.serviceFromDate,
        serviceToDate: serviceVerificationAudit.serviceToDate,
        quantity: serviceVerificationAudit.quantity,
        totalPaidAmount: serviceVerificationAudit.totalPaidAmount,
        status: '',
        serviceVerificationDate: serviceVerificationAudit.serviceVerificationDate,
        reviewerFullName: serviceVerificationAudit.reviewerFullName,
        specialistFullName: serviceVerificationAudit.specialistFullName,
        additionalRecipients: serviceVerificationAudit.additionalRecipients,
        internalNotes: serviceVerificationAudit.internalNotes,
        file: [],
        removeFile: [],
        uploadedFile: serviceVerificationAudit.attachment,
        activity: serviceVerificationAudit.activity,
        followUp: serviceVerificationAudit.followUp,
        documentation: serviceVerificationAudit.documentation,
        comments: serviceVerificationAudit.comments
      } : initialServiceVerificationAudit

      setInitialServiceVerificationAuditValues(serviceVerificationData)
    }, [serviceVerificationAudit])

    const columns = [{
      dataField: 'date',
      text: 'Date',
      sort: true,
      headerStyle: { width: 150 },
      style: { width: 150 },
      // eslint-disable-next-line react/display-name
      formatter: (cellContent) => ( cellContent && moment.utc(cellContent).format('MM/DD/YYYY') ) || ''
    }, {
      dataField: 'activityType',
      text: 'Type',
      sort: true,
      headerStyle: { width: 150 },
      style: { width: 150 },
    }, {
      dataField: 'activity',
      text: 'Content',
      sort: true,
    }, {
      dataField: 'username',
      text: 'Author Email',
      sort: true,
    }]

    return (
      <Modal size='xl' show={show} backdrop='static' onHide={onClose}>
        <Modal.Header closeButton id='modal-header'>
          <FontAwesomeIcon icon={['fas', 'edit']} size='lg' className='modal-header-icon' />
          <Modal.Title id='modal-header-title' style={{ width: '100%' }} >
            <div style={{ display: 'flex', flexFlow: 'row', justifyContent: 'space-between', width: '100%' }}>
              <span>Service Verification Audit</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {
            updatingServiceVerificationAudit ? (
              updateServiceVerificationAudit.loading || updateServiceVerificationAudit.error !== false ? (
                <div className='modal-loading-container'>
                  {loadingIcon}
                  <span className='modal-loading-text' style={updateServiceVerificationAudit.error !== false ? { width: '100%' } : {}}>{modalText}</span>
                  {
                    updateServiceVerificationAudit.error !== false ? (<span style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: errorText }} />) : null
                  }
                </div>
              ) : updateServiceVerificationAudit.success !== false ? (
                <div>
                  <div className='modal-success-container'>
                    <FontAwesomeIcon icon={['far', 'check-circle']} color={mainColors.successGreen} className='modal-success-icon' />
                    <span className='modal-loading-text'>Success!</span>
                    <span className='modal-success-text'>You have submitted the Service Verification Audit for review.</span>
                  </div>
                </div>
              ) : (
                <div></div>
              )
            ) : (
              <Formik
                initialValues={initialServiceVerificationAuditValues}
                enableReinitialize
                validationSchema={ServiceVerificationAuditSchema}
                onSubmit={(values, { setSubmitting }) => {
                  setSubmitting(false)
                  onSubmit(values)
                }}
              >
                {({ errors, values, handleChange, handleSubmit, setFieldValue }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Card className='service-verification-form-card'>
                      <Card.Body className='service-verification-form-card-body'>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Provider ID</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerName'
                              value={`${values.providerId || ''}`}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Date of Service Verification Audit</Form.Label>
                            <DatePicker
                              className='disabledDatePicker datepicker'
                              dateFormat='MM/dd/yyyy'
                              selected={values.serviceVerificationDate ? getDateWithoutTZOffset(values.serviceVerificationDate) : null}
                              onChange={() => {}}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Organization Name</Form.Label>
                            <Form.Control
                              type='text'
                              name='organizationName'
                              value={values.organizationName || ''}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Provider Name</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerName'
                              value={values.providerName || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Claim Number</Form.Label>
                            <Form.Control
                              type='text'
                              name='claimNumber'
                              value={values.claimNumber || ''}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Authorization Number</Form.Label>
                            <Form.Control
                              type='text'
                              name='authorizationNumber'
                              value={values.authorizationNumber || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Member Name</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerCity'
                              value={`${values.firstName || ''} ${values.middleName || ''} ${values.lastName || ''}`}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Email Address</Form.Label>
                            <Form.Control
                              type='text'
                              name='emailAddress'
                              value={values.emailAddress || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Service From</Form.Label>
                            <DatePicker
                              className='disabledDatePicker datepicker'
                              dateFormat='MM/dd/yyyy'
                              selected={values.serviceFromDate ? getDateWithoutTZOffset(values.serviceFromDate) : null}
                              onChange={(date: Date) => setFieldValue('serviceFromDate', getDateWithoutTZOffset(date))}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Service To</Form.Label>
                            <DatePicker
                              className='disabledDatePicker datepicker'
                              dateFormat='MM/dd/yyyy'
                              selected={values.serviceToDate ? getDateWithoutTZOffset(values.serviceToDate) : null}
                              onChange={(date: Date) => setFieldValue('serviceToDate', getDateWithoutTZOffset(date))}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Service Type</Form.Label>
                            <Form.Control
                              type='text'
                              name='serviceType'
                              value={values.serviceType || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>LCI Personnel Conducting Service Audit</Form.Label>
                            <Form.Control
                              type='text'
                              name='reviewerFullName'
                              value={values.reviewerFullName || ''}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Assigned Provider Specialist</Form.Label>
                            <Form.Control
                              type='text'
                              name='specialistFullName'
                              value={values.specialistFullName || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Additional Email Recipient(s)</Form.Label>
                            <Form.Control
                              type='text'
                              name='additionalRecipients'
                              value={values.additionalRecipients || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                      </Card.Body>
                    </Card>
                    <Card className='service-verification-form-card'>
                      <Card.Body className='service-verification-form-card-body'>
                        <Card.Title>Documentation</Card.Title>
                        <span style={{ fontWeight: 'bold' }} dangerouslySetInnerHTML={{__html: `Examples of Supporting Documentation: ${values.documentation || ''}` }}></span>
                        <Row>
                          <Form.Group as={Col} xs={12}>
                            <FileUpload
                              error={errors.file}
                              files={values.file}
                              id='serviceVerificationAuditAttachmentId'
                              name='file'
                              placeholder='Locate your file(s) to upload'
                              readonly={readonly}
                              removeListName='removeFile'
                              uploadedFile={values.uploadedFile}
                              urlDirectory='pdf/serviceverification'
                              setFieldValue={setFieldValue}
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} xs={12}>
                            <Form.Label>Comments (Optional)</Form.Label>
                            <Form.Control
                              name='comments'
                              value={values.comments || ''}
                              as='textarea'
                              rows={4}
                              onChange={handleChange}
                              isInvalid={!!errors.comments}
                              disabled={readonly}
                            />
                            <Form.Control.Feedback type='invalid'>
                              {errors.comments}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                      </Card.Body>
                    </Card>
                    {
                      values.activity && values.activity.length > 0 && (
                        <Card className='service-verification-form-card'>
                          <Card.Body className='service-verification-form-card-body'>
                            <Card.Title>Approval Process Activity Log</Card.Title>
                            <Form.Group as={Col} xs={12} style={{ marginTop: 20 }}>  
                              <div>
                                <ToolkitProvider
                                  keyField='id'
                                  bootstrap4
                                  data={ values.activity.concat(values.followUp).sort((a, b) => moment(b.date).diff(moment(a.date))).map(activity => ({...activity, id: `${activity.id}_${activity.activityType}`})) }
                                  columns={ columns }
                                  search
                                >
                                  {
                                    props => (
                                      <div style={{width: '100%'}}>
                                        <BootstrapTable
                                          striped
                                          condensed
                                          { ...props.baseProps }
                                        />
                                      </div>
                                    )
                                  }
                                </ToolkitProvider>
                              </div>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      )
                    }
                    <div className='service-verification-btn-container'>
                      <Button
                        variant='secondary'
                        className='custom-button mr-2'
                        onClick={onClose}
                      >
                        Cancel
                      </Button>
                      {
                        !readonly && (
                          <Button
                            variant='info'
                            type='submit'
                          >
                            Submit
                          </Button>
                        )
                      }
                    </div>
                  </Form>
                )}
              </Formik>
            )
          }
        </Modal.Body>
      </Modal>
    )
  }

export default ServiceVerificationAuditModal